import {
  FeatureName,
  MediaData,
  MediaPickerComponent,
  MediaType,
  NewMessage,
  PickerMode,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { IconButton } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { AddButton } from '../crop-circle/AddButton';
import {
  CameraIcon,
  DocumentIcon,
  ImageIcon,
} from '../crop-circle/icons';
import { colors as CropCircleColors } from '../crop-circle/tokens/colors';
import { RenderMediaItem } from '../media/renderMediaItem';
import { useTypedMedia } from '../media/useTypedMedia';
import { MentionInput } from '../mentionInputField';
import { IssueFooterOld } from './issueFooter-old';

type IssueFooterProps = {
  onNewMessage: (message: NewMessage) => Promise<void>;
  mediaPicker: MediaPickerComponent;
  acceptedMediaTypes?: MediaType[];
  workspaceId?: string;
  mode?: PickerMode;
  containerStyle?: StyleProp<ViewStyle>;
  maxImagePreviewWidth?: number;
  focusOnMessage?: boolean;
  pickDocument?: (callbacks: {
    setUploading: (uploading: boolean) => void;
    onProgress: (mediaId: string, progress: number) => void;
  }) => Promise<MediaData | null>;
};

// TODO: Crop Circle Redesign | remove wrapper once feature is fully rolled out
export const IssueFooter = (props: IssueFooterProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (!isCropCircleRedesignEnabled) {
    return <IssueFooterOld {...props} />;
  }

  return <IssueFooterBase {...props} />;
};

const IssueFooterBase = (props: IssueFooterProps) => {
  const { t } = useTranslation();
  const {
    handleAdd,
    handleUploadProgress,
    typedMedia: messageMedia,
    handleDelete,
    reset: resetMessageMedia,
  } = useTypedMedia([]);

  const [message, setMessage] = useState('');
  const [canSend, setCanSend] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [showPreviewGallery, setShowThumbnailPreview] = useState(false);
  const [isMultiline, setIsMultiline] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputHeight, setInputHeight] = useState<number>(
    minHeightInputContainerPhone
  );

  const cameraOpenRef = useRef<(() => void) | null>(null);
  const galleryOpenRef = useRef<(() => void) | null>(null);
  const isPickingDocumentRef = useRef(false);

  // Always keep inputHeight at or above the pill's minimum so the TextInput
  // (which receives this value as an explicit height) never shrinks the pill.
  // onContentSizeChange reports ~20 px for a single line which would otherwise
  // collapse the 56 px container on the first keystroke.
  const handleSetInputHeight = useCallback(
    (h: number) => setInputHeight(Math.max(h, minHeightInputContainerPhone)),
    []
  );

  const MediaPicker = props.mediaPicker;
  const pickerMode = props.mode || PickerMode.CAMERA;
  let pickerIcon;

  const messageInputRef = useRef(null);

  // Footer height grows with the input on both platforms.
  // paddingVertical: 16 (top + bottom = 32) accounts for the extra space.
  const footerHeight = inputHeight + 32;

  const hasVideoInPreview = messageMedia.some(
    (m) => m.type === MediaType.Video
  );
  const previewAreaHeight = hasVideoInPreview
    ? mediaContainerHeightWithVideo
    : mediaContainerHeight;

  switch (pickerMode) {
    case PickerMode.PICKER:
      pickerIcon = 'image-outline';
      break;
    case PickerMode.CAMERA:
      pickerIcon = 'camera-outline';
      break;
    case PickerMode.ATTACHMENT:
      pickerIcon = 'paperclip';
      break;
    default:
      pickerIcon = 'camera-outline';
      break;
  }

  // Crop circle redesign always uses plus icon
  pickerIcon = 'plus';

  useEffect(() => {
    setCanSend((messageMedia.length > 0 && !isUploading) || message.length > 0);
  }, [messageMedia, message, isUploading]);

  useEffect(() => {
    setShowThumbnailPreview(messageMedia.length > 0);
  }, [messageMedia]);

  const send = () => {
    dismissKeyboard();
    props.onNewMessage({
      message: message,
      media: messageMedia,
    });
    setMessage('');
    resetMessageMedia();
    setIsMultiline(false);
    // Reset pill height immediately so the footer snaps back to the single-line
    // size in the same render cycle, without waiting for onContentSizeChange.
    setInputHeight(minHeightInputContainerPhone);
  };

  const dismissKeyboard = () => {
    messageInputRef.current?.blur();
  };

  const deleteMediaItem = (mediaId: string) => {
    handleDelete(mediaId);
  };

  const closeMenu = () => setMenuOpen(false);

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
      return;
    }
    dismissKeyboard();
    setMenuOpen(true);
  };

  const onTapCamera = () => {
    closeMenu();
    cameraOpenRef.current?.();
  };

  const onTapGallery = () => {
    closeMenu();
    galleryOpenRef.current?.();
  };

  const onTapFile = async () => {
    closeMenu();
    if (isPickingDocumentRef.current || !props.pickDocument) return;
    isPickingDocumentRef.current = true;
    try {
      const mediaItem = await props.pickDocument({
        setUploading,
        onProgress: handleUploadProgress,
      });
      if (mediaItem) handleAdd(mediaItem);
    } finally {
      isPickingDocumentRef.current = false;
    }
  };

  return (
    <View
      style={[
        {
          minHeight: showPreviewGallery
            ? footerHeight + previewAreaHeight + 10
            : footerHeight,
          height: showPreviewGallery
            ? footerHeight + previewAreaHeight + 10
            : footerHeight,
          paddingTop: 0,
          paddingBottom: 8,
        },
        styles.footer,
        props.containerStyle,
      ]}
    >
      {showPreviewGallery && (
        <View
          style={{
            width: '100%',
            paddingLeft: 64,
            paddingRight: 16,
            marginTop: 10,
          }}
        >
          <View
            style={[
              styles.mediaContainer,
              {
                flexDirection: 'row',
                height: previewAreaHeight,
                minHeight: previewAreaHeight,
              },
            ]}
          >
            {messageMedia.length > 0 && (
              <FlatList
                horizontal={true}
                data={messageMedia}
                style={styles.mediaList}
                renderItem={({ item }) => (
                  <View style={styles.mediaItemContainer} key={item.id}>
                    <RenderMediaItem
                      mediaItem={item}
                      key={item.id}
                      deleteMediaItem={deleteMediaItem}
                      hasLightbox
                      canDelete
                      displayWidth={
                        item.type === MediaType.Video ? 200 : undefined
                      }
                    />
                  </View>
                )}
              ></FlatList>
            )}
          </View>
        </View>
      )}

      <View
        style={[
          styles.footerContainer,
          { alignItems: isMultiline ? 'flex-end' : 'center' },
        ]}
      >
        <View style={styles.imagePickerContainer}>
          <AddButton
            onPress={toggleMenu}
            active={menuOpen}
            accessibilityLabel={
              menuOpen ? 'Close media picker' : 'Open media picker'
            }
          />

          <View style={styles.headlessPicker} pointerEvents="none">
            <MediaPicker
              iconColor={Colors.white}
              cardStyle={'contained'}
              mode={PickerMode.CAMERA}
              style={styles.imagePickerIcon}
              icon={pickerIcon}
              iconSize={30}
              setMedia={handleAdd}
              setProgress={handleUploadProgress}
              setUploading={setUploading}
              workspaceId={props.workspaceId}
              dismissKeyboard={dismissKeyboard}
              acceptedMediaTypes={props.acceptedMediaTypes}
              onPickerReady={(opener) => {
                cameraOpenRef.current = opener;
              }}
            />
            <MediaPicker
              iconColor={Colors.white}
              cardStyle={'contained'}
              mode={PickerMode.PICKER}
              style={styles.imagePickerIcon}
              icon={pickerIcon}
              iconSize={30}
              setMedia={handleAdd}
              setProgress={handleUploadProgress}
              setUploading={setUploading}
              workspaceId={props.workspaceId}
              dismissKeyboard={dismissKeyboard}
              acceptedMediaTypes={props.acceptedMediaTypes}
              onPickerReady={(opener) => {
                galleryOpenRef.current = opener;
              }}
            />
          </View>

          {menuOpen && (
            <>
              <Pressable
                style={styles.menuBackdrop}
                onPress={closeMenu}
                accessibilityRole="button"
                accessibilityLabel="Close media picker"
              />
              <View style={styles.menuPill}>
                <Pressable
                  style={styles.menuItem}
                  onPress={onTapCamera}
                  accessibilityRole="button"
                  accessibilityLabel="Open camera"
                >
                  <CameraIcon size={24} color={CropCircleColors.brand.white} />
                </Pressable>
                <Pressable
                  style={styles.menuItem}
                  onPress={onTapGallery}
                  accessibilityRole="button"
                  accessibilityLabel="Open gallery"
                >
                  <ImageIcon size={24} color={CropCircleColors.brand.white} />
                </Pressable>
                <Pressable
                  style={styles.menuItem}
                  onPress={onTapFile}
                  accessibilityRole="button"
                  accessibilityLabel="Attach file"
                >
                  <DocumentIcon
                    size={24}
                    color={CropCircleColors.brand.white}
                  />
                </Pressable>
              </View>
            </>
          )}
        </View>
        <View
          style={[
            styles.messageContainer,
            {
              borderRadius: isMultiline
                ? inputContainerBorderRadiusMultiline
                : inputContainerBorderRadiusPhone,
              height: inputHeight,
              // Prevent the pill from shrinking below the initial single-line
              // height when onContentSizeChange reports a small value (~39 px
              // for the first line before the formula adds its padding offset).
              minHeight: minHeightInputContainerPhone,
            },
          ]}
        >
          <View style={styles.inputContainer}>
            <MentionInput
              inputFieldText={message}
              setInputFieldText={setMessage}
              setIsMultiline={setIsMultiline}
              inputHeight={inputHeight}
              setInputHeight={handleSetInputHeight}
              send={send}
              useCropCircle
              placeholder={t('common.message')}
            />
          </View>
          {canSend && (
            <View style={styles.sendMessage}>
              <IconButton
                icon="close"
                iconColor={Colors.white}
                style={{
                  margin: 0,
                  width: 32,
                  height: 32,
                  backgroundColor: CropCircleColors.neutral[400],
                  borderRadius: 99,
                }}
                onPress={() => {
                  setMessage('');
                  resetMessageMedia();
                  setIsMultiline(false);
                  setInputHeight(minHeightInputContainerPhone);
                }}
                size={16}
              />
              <IconButton
                icon="arrow-up"
                iconColor={Colors.white}
                style={{
                  margin: 0,
                  width: 32,
                  height: 32,
                  backgroundColor: Colors.black,
                  borderRadius: 99,
                }}
                onPress={send}
                size={18}
              />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const inputContainerBorderRadiusPhone = 99;
const inputContainerBorderRadiusMultiline = 16;
const mediaContainerHeight = 120;
/** Fits 200px-wide video preview + controls (200*9/16 + 64 ≈ 176) with margin */
const mediaContainerHeightWithVideo = 200;
const minHeightInputContainerPhone = 56;

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
  },
  imagePickerContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: 56,
    width: 56,
  },
  imagePickerIcon: {
    height: 56,
    maxHeight: 56,
    width: 56,
    backgroundColor: CropCircleColors.brand.core,
    borderRadius: 99,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headlessPicker: {
    position: 'absolute',
    top: -9999,
    left: -9999,
    width: 0,
    height: 0,
    opacity: 0,
  },
  menuBackdrop: {
    position: 'absolute',
    top: -2000,
    left: -2000,
    right: -2000,
    bottom: -2000,
    zIndex: 1,
  },
  menuPill: {
    position: 'absolute',
    bottom: 64,
    left: 4,
    width: 48,
    paddingVertical: 12,
    backgroundColor: CropCircleColors.neutral[700],
    borderRadius: 24,
    alignItems: 'center',
    gap: 8,
    zIndex: 2,
  },
  menuItem: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-end',
    paddingBottom: 12,
    gap: 8,
  },
  messageContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 99,
    paddingLeft: 24,
    paddingRight: 12,
  },
  inputContainer: {
    flex: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaContainer: {
    height: mediaContainerHeight,
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: 12,
    paddingLeft: 16,
    paddingBottom: 16,
    backgroundColor: Colors.primaryGrayBackground,
  },
  footer: {
    backgroundColor: Colors.brand.white,
    ...Platform.select({
      default: {
        minHeight: 60,
      },
    }),
  },
  footerContainer: {
    flexDirection: 'row' as const,
    flex: 1,
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  mediaList: {
    width: '100%',
    height: '100%',
    flex: 1,
  },
  mediaItemContainer: {
    alignSelf: 'center',
    marginLeft: 8,
  },
});
